// client/src/components/media/InstagramGridPlanner.jsx
// Visual planner for Instagram: the account's recent published grid plus the
// upcoming scheduled posts, laid out 3-wide the way the feed will look.
//
// Published media comes straight from the Graph API (instagram_basic, already
// approved). Scheduled posts come from the same /api/schedule/upcoming feed the
// Scheduled Posts list uses. Scheduled tiles are clickable — they open the
// post's editor. Reordering scheduled slots is a planned follow-up.

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppFooter from '../AppFooter';
import { FaArrowLeft, FaHome, FaInstagram } from 'react-icons/fa';
import { fetchUpcomingScheduledPosts } from '../../services/scheduledPostsApi';
import { getInstagramPublishingLimit } from '../../services/metaFeatures';

const baseUrl = process.env.REACT_APP_BASE_URL || '';

function isVideoUrl(u = '') {
  return /\.(mp4|m4v|mov|webm)(\?|$)/i.test(String(u));
}

export default function InstagramGridPlanner() {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const tenantId = Number(tenant);

  const [published, setPublished] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [limit, setLimit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      fetch(`${baseUrl}/api/integrations/instagram/media?tenantId=${tenantId}&limit=18`, { credentials: 'include' })
        .then(r => r.json()),
      fetchUpcomingScheduledPosts(tenantId, { limit: 50 }),
      getInstagramPublishingLimit(tenantId),
    ]).then(([mediaRes, schedRes, limitRes]) => {
      if (cancelled) return;
      if (mediaRes.status === 'fulfilled' && mediaRes.value?.success) {
        setPublished(mediaRes.value.media || []);
      } else if (mediaRes.status === 'fulfilled' && mediaRes.value?.error) {
        setError(mediaRes.value.error);
      }
      if (schedRes.status === 'fulfilled') {
        const posts = schedRes.value?.posts || schedRes.value || [];
        setScheduled(
          posts.filter(p => {
            const pf = Array.isArray(p.platforms) ? p.platforms : [];
            return pf.some(x => String(x).toLowerCase() === 'instagram');
          })
        );
      }
      if (limitRes.status === 'fulfilled') setLimit(limitRes.value);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [tenantId]);

  // Upcoming: soonest first (that's the next tile to enter the feed).
  const upcoming = useMemo(() => {
    return [...scheduled].sort((a, b) => {
      const da = new Date(a.scheduled_on || a.scheduledOn || 0).getTime();
      const db = new Date(b.scheduled_on || b.scheduledOn || 0).getTime();
      return da - db;
    });
  }, [scheduled]);

  const tileStyle = {
    position: 'relative',
    width: '100%',
    aspectRatio: '1 / 1',
    background: '#f0f0f0',
    overflow: 'hidden',
    borderRadius: 4,
  };
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 4,
    maxWidth: 560,
  };

  const mediaThumb = (m) => {
    const url = m.media_type === 'VIDEO' ? (m.thumbnail_url || m.media_url) : m.media_url;
    return url
      ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
      : <div style={{ padding: 8, fontSize: 11, color: '#888' }}>{(m.caption || '').slice(0, 60)}</div>;
  };

  const schedThumb = (p) => {
    const cover = Array.isArray(p.media_urls) && p.media_urls.length ? p.media_urls[0] : null;
    if (cover && !isVideoUrl(cover)) {
      return <img src={cover} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />;
    }
    return (
      <div style={{ padding: 8, fontSize: 11, color: '#555', display: 'flex', alignItems: 'center', height: '100%' }}>
        {(p.content || '(no caption)').slice(0, 80)}
      </div>
    );
  };

  return (
    <div style={{ padding: 20, paddingBottom: 80, maxWidth: 640, margin: '0 auto' }}>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <FaInstagram style={{ color: '#C13584' }} /> Instagram Grid Planner
      </h2>

      {limit && (
        <p className="text-muted" style={{ marginTop: -4 }}>
          {limit.usage}/{limit.total} posts published in the last 24h
        </p>
      )}
      {error && <div className="alert alert-warning py-2">{error}</div>}
      {loading && <p>Loading…</p>}

      {!loading && (
        <>
          <h5 style={{ marginTop: 16 }}>Upcoming ({upcoming.length})</h5>
          {upcoming.length === 0 && <p className="text-muted">No scheduled Instagram posts.</p>}
          <div style={gridStyle}>
            {upcoming.map(p => (
              <button
                key={`s-${p.id}`}
                onClick={() => navigate(`/${tenant}/media/scheduled-posts`)}
                title={`Scheduled ${new Date(p.scheduled_on || p.scheduledOn).toLocaleString()}`}
                style={{ ...tileStyle, border: '2px dashed #C13584', cursor: 'pointer', padding: 0 }}
              >
                {schedThumb(p)}
                <span style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  background: 'rgba(193,53,132,0.85)', color: '#fff', fontSize: 10, padding: '2px 4px',
                }}>
                  {new Date(p.scheduled_on || p.scheduledOn).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </span>
              </button>
            ))}
          </div>

          <h5 style={{ marginTop: 24 }}>Published ({published.length})</h5>
          {published.length === 0 && <p className="text-muted">No published Instagram media found.</p>}
          <div style={gridStyle}>
            {published.map(m => (
              <a
                key={m.id}
                href={m.permalink}
                target="_blank"
                rel="noopener noreferrer"
                style={tileStyle}
              >
                {mediaThumb(m)}
                {(m.media_type === 'VIDEO' || m.media_type === 'CAROUSEL_ALBUM') && (
                  <span style={{
                    position: 'absolute', top: 4, right: 4, color: '#fff', fontSize: 12,
                    textShadow: '0 0 3px rgba(0,0,0,0.6)',
                  }}>
                    {m.media_type === 'VIDEO' ? '▶' : '▦'}
                  </span>
                )}
              </a>
            ))}
          </div>
        </>
      )}

      <AppFooter
        buttons={[
          { icon: <FaArrowLeft />, label: 'Back', onClick: () => navigate(-1) },
          { icon: <FaHome />, label: 'Home', onClick: () => navigate(`/${tenant}/dashboard`) },
        ]}
      />
    </div>
  );
}
